<template>
  <section class="guided-mode__card">
    <guided-mode-visual
      :media="media"
      :fallback-icon="mdiImageOutline"
      :playback-rate="playbackRate"
      :size="mediaSize"
      :interactive="mediaInteractive"
      :audio-control="mediaAudioControl"
      :audio-enabled="audioEnabled"
      :audio-label="String($t('app.assessment_mode.guided.media.sound'))"
      @toggle-size="$emit('toggle-size')"
      @toggle-audio="$emit('toggle-audio')"
    />
    <p class="guided-mode__prompt">{{ prompt }}</p>
    <p v-if="instructions" class="guided-mode__instructions">{{ instructions }}</p>
    <option-input
      v-if="collectionType === 'option'"
      :options="options"
      :value="selectedValues"
      :multiple="multiple"
      :class="{
        'guided-mode__option--warning': validationSeverity === 'warning',
        'guided-mode__option--invalid': isBlockingValidation,
      }"
      @select="$emit('option-selected', $event)"
    />
    <div
      v-else-if="collectionType === 'numeric'"
      class="guided-mode__numeric"
      :class="{
        'guided-mode__numeric--warning': validationSeverity === 'warning',
        'guided-mode__numeric--invalid': isBlockingValidation,
      }"
      :data-widget="numericWidget"
    >
      <numeric-slider-input
        :value="numericValue"
        :minimum="minimum"
        :maximum="maximum"
        :step="numericStep"
        :unit="displayUnit"
        :label="prompt"
        :input-id="inputId"
        :allow-unknown="allowUnknown"
        :unknown-label="unknownLabel"
        :unknown-display="unknownDisplay"
        @input="$emit('answer', $event)"
        @entry-complete="$emit('entry-complete')"
      />
      <p
        class="guided-mode__validation-message"
        :class="{ 'guided-mode__validation-message--warning': validationSeverity === 'warning' }"
        role="alert"
        :aria-hidden="validationMessageToken ? 'false' : 'true'"
      >
        {{ validationMessageToken ? $t(validationMessageToken) : '' }}
      </p>
    </div>
    <assessment-text-input
      v-else
      :value="textValue"
      :label="prompt"
      :placeholder="prompt"
      :input-id="inputId"
      :class="{
        'guided-mode__text--warning': validationSeverity === 'warning',
        'guided-mode__text--invalid': isBlockingValidation,
      }"
      @input="$emit('answer', $event)"
      @entry-complete="$emit('entry-complete')"
    />
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mdiImageOutline } from '@mdi/js'
import {
  NumericSliderInput,
  OptionInput,
  TextInput as AssessmentTextInput,
} from '@/components/assessment-inputs'
import type { AssessmentInputOption } from '@/components/assessment-inputs'
import GuidedModeVisual from './GuidedModeVisual.vue'
import type {
  GuidedAnswer,
  GuidedModeCollection,
  GuidedModeMedia,
  GuidedValidationSeverity,
} from '../types/guided-mode.types'

export default defineComponent({
  name: 'GuidedModeQuestion',
  components: {
    AssessmentTextInput,
    GuidedModeVisual,
    NumericSliderInput,
    OptionInput,
  },
  props: {
    prompt: { type: String, required: true },
    instructions: { type: String, default: '' },
    options: { type: Array as PropType<AssessmentInputOption[]>, default: () => [] },
    collection: { type: Object as PropType<GuidedModeCollection>, required: true },
    answer: { type: [String, Number, Array] as PropType<GuidedAnswer>, default: null },
    selectedValues: { type: Array as PropType<string[]>, default: () => [] },
    multiple: { type: Boolean, default: false },
    displayUnit: { type: String, default: '' },
    widget: { type: String, default: '' },
    numericStep: { type: Number, default: 1 },
    inputId: { type: String, required: true },
    media: { type: Object as PropType<GuidedModeMedia | null>, default: null },
    playbackRate: { type: Number, default: 1 },
    mediaSize: { type: String, default: 'default' },
    mediaInteractive: { type: Boolean, default: false },
    mediaAudioControl: { type: Boolean, default: false },
    audioEnabled: { type: Boolean, default: false },
    validationSeverity: {
      type: String as PropType<GuidedValidationSeverity | null>,
      default: null,
    },
    validationMessageToken: { type: String, default: '' },
  },
  emits: ['answer', 'option-selected', 'toggle-size', 'toggle-audio', 'entry-complete'],
  data: () => ({ mdiImageOutline }),
  computed: {
    collectionType(): string {
      return this.collection.type || ''
    },
    minimum(): number {
      return Number(this.collection.min)
    },
    maximum(): number {
      return Number(this.collection.max)
    },
    numericValue(): string | number | null {
      return typeof this.answer === 'string' || typeof this.answer === 'number' ? this.answer : null
    },
    textValue(): string | number | null {
      return typeof this.answer === 'string' || typeof this.answer === 'number' ? this.answer : null
    },
    allowUnknown(): boolean {
      return this.collection.allowUnknown === true
    },
    numericWidget(): string {
      return this.widget === 'tapper' ? 'tapper' : 'slider'
    },
    isBlockingValidation(): boolean {
      return this.validationSeverity === 'blocking' || this.validationSeverity === 'unconfigured'
    },
    unknownLabel(): string {
      return String(this.$t('app.inputs.unknown.button'))
    },
    unknownDisplay(): string {
      return String(this.$t('responses.generic.unknown.title'))
    },
  },
})
</script>
